import { AlertTriangle, OctagonAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import {
  translateImportFieldLabel,
  translateImportWarning,
} from "@/lib/import/import-server-i18n";
import { ImportAnalysisReport } from "@/lib/import/import-types";

/**
 * Avertissements de l'analyse.
 *
 * C'est l'implementation concrete de « si un doute, arreter et demander » :
 *
 *   - un avertissement **bloquant** (doublons de n° dans le fichier, colonne
 *     obligatoire absente, fichier trop long, colonnes en double) interdit de
 *     continuer, et la raison est nommee ;
 *   - un avertissement **non bloquant** n'interdit rien mais exige une prise de
 *     connaissance explicite, pour qu'il ne soit pas simplement survole.
 */
export const ImportBlockersBanner = ({
  report,
  acknowledged,
  onAcknowledgedChange,
}: {
  report: ImportAnalysisReport;
  acknowledged: boolean;
  onAcknowledgedChange: (value: boolean) => void;
}) => {
  const vt = useVisibleT();
  const { t } = useTranslation();

  const blocking = report.warnings.filter((warning) => warning.blocking);
  const advisory = report.warnings.filter((warning) => !warning.blocking);

  const missing = report.missingRequired;
  const duplicates = report.duplicates.withinFile;

  if (blocking.length === 0 && advisory.length === 0 && missing.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3">
      {(blocking.length > 0 || missing.length > 0) && (
        <div
          role="alert"
          className="flex gap-3 p-4 rounded-xl border border-red-300 bg-red-50"
        >
          <OctagonAlert className="w-5 h-5 text-red-600 shrink-0 mt-0.5" aria-hidden />
          <div className="flex flex-col gap-2 text-sm">
            <p className="font-semibold text-red-900">
              {vt(
                "bo.import.blockers.title",
                "Votre fichier ne peut pas être importé en l'état",
              )}
            </p>
            <ul className="flex flex-col gap-1 text-red-900">
              {missing.length > 0 && (
                <li>
                  {vt(
                    "bo.import.blockers.missingRequired",
                    "Colonne obligatoire absente :",
                  )}{" "}
                  {missing
                    .map((field) =>
                      translateImportFieldLabel(t, field.fieldKey, field.label),
                    )
                    .join(", ")}
                </li>
              )}
              {blocking.map((warning) => (
                <li key={warning.code}>{translateImportWarning(t, warning)}</li>
              ))}
            </ul>

            {duplicates.length > 0 && (
              <details className="text-red-900">
                <summary className="cursor-pointer">
                  {vt("bo.import.blockers.seeDuplicates", "Voir les numéros en double")}
                </summary>
                <ul className="mt-1 flex flex-col gap-0.5 pl-4 list-disc">
                  {duplicates.slice(0, 20).map((duplicate) => (
                    <li key={duplicate.matchKey}>
                      <span className="font-mono">{duplicate.matchKey}</span>
                      {" — "}
                      {vt("bo.import.blockers.atRows", "lignes")}{" "}
                      {duplicate.rowIndexes.join(", ")}
                    </li>
                  ))}
                  {duplicates.length > 20 && (
                    <li>
                      {vt("bo.import.blockers.andMore", "et")}{" "}
                      {duplicates.length - 20}…
                    </li>
                  )}
                </ul>
              </details>
            )}

            <p className="text-red-800">
              {vt(
                "bo.import.blockers.howTo",
                "Corrigez ces points dans votre fichier, puis déposez-le à nouveau.",
              )}
            </p>
          </div>
        </div>
      )}

      {advisory.length > 0 && (
        <div className="flex gap-3 p-4 rounded-xl border border-amber-300 bg-amber-50">
          <AlertTriangle
            className="w-5 h-5 text-amber-600 shrink-0 mt-0.5"
            aria-hidden
          />
          <div className="flex flex-col gap-2 text-sm">
            <p className="font-semibold text-amber-900">
              {vt("bo.import.blockers.advisoryTitle", "À vérifier avant de continuer")}
            </p>
            <ul className="flex flex-col gap-1 text-amber-900">
              {advisory.map((warning) => (
                <li key={warning.code}>{translateImportWarning(t, warning)}</li>
              ))}
            </ul>
            <label className="flex items-center gap-2 text-amber-900 cursor-pointer">
              <Checkbox
                checked={acknowledged}
                onCheckedChange={(value) => onAcknowledgedChange(value === true)}
              />
              <span>{vt("bo.import.blockers.acknowledge", "J'ai compris")}</span>
            </label>
          </div>
        </div>
      )}
    </div>
  );
};
